import { useState, useEffect } from 'react';
import {
  getAccessToken,
  getUser,
  getUserPrimaryRole,
  getUserTenantId,
} from '../services/auth';
import { normalizeApiUrl } from '../utils/api-base-url';

// Per-deployment default tenant. Single-tenant on-prem boxes set
// VITE_DEFAULT_TENANT_ID to their own tenant so the dock "Tenant Landing"
// quick-link and any pre-resolution fallback point at the right tenant
// instead of the generic 00000000 seed tenant.
const DEFAULT_TENANT_ID =
  import.meta.env.VITE_DEFAULT_TENANT_ID || '00000000-0000-0000-0000-000000000001';

async function resolveTenantContextViaGateway(
  accessToken: string,
  userId: string,
  userRole: string,
): Promise<Partial<TenantContextState> | null> {
  try {
    const response = await fetch(`${normalizeApiUrl(import.meta.env.VITE_API_URL)}/tenant/resolve`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'X-User-ID': userId,
        'X-User-Role': userRole,
      },
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json() as {
      tenant_id?: unknown;
      user_role?: unknown;
    };

    return {
      tenantId: typeof data.tenant_id === 'string' && data.tenant_id.trim()
        ? data.tenant_id
        : undefined,
      userRole: typeof data.user_role === 'string' && data.user_role.trim()
        ? data.user_role
        : undefined,
    };
  } catch {
    return null;
  }
}

interface TenantContextState {
  tenantId: string;
  userRole: string;
  isResolved: boolean;
}

export function useTenantContext(): TenantContextState {
  const [tenantId, setTenantId] = useState<string>(DEFAULT_TENANT_ID);
  const [userRole, setUserRole] = useState<string>('user');
  const [isResolved, setIsResolved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const user = await getUser();
      let nextTenantId = getUserTenantId(user);
      let nextUserRole = getUserPrimaryRole(user);
      const accessToken = await getAccessToken();
      const userId = typeof user?.profile?.sub === 'string' ? user.profile.sub : '';

      if (accessToken && userId) {
        const gatewayContext = await resolveTenantContextViaGateway(accessToken, userId, nextUserRole);
        if (gatewayContext?.tenantId) {
          nextTenantId = gatewayContext.tenantId;
        }
        if (gatewayContext?.userRole) {
          nextUserRole = gatewayContext.userRole;
        }
      }

      if (cancelled) {
        return;
      }

      setTenantId(nextTenantId);
      setUserRole(nextUserRole);
    })()
      .finally(() => {
        if (!cancelled) {
          setIsResolved(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return { tenantId, userRole, isResolved };
}

export function useTenantId(): string {
  return useTenantContext().tenantId;
}

export function getTenantId(): string {
  // Synchronous fallback - returns default, caller should prefer useTenantId() hook
  return DEFAULT_TENANT_ID;
}

export default useTenantId;
